import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { errorMessage } from '../lib/format';
import {
  fileToAttachment,
  sendEmail,
  type EmailAttachment,
  type Recipient,
  type SendEmailResult,
} from '../lib/email';

// One compose dialog behind every "send an email" button in the app:
// class blasts, session reminders, schedule-change notices, and the
// liability-waiver send. The caller supplies the recipient list and a
// prefilled subject/message; this handles editing, attachments, sending
// (per-recipient, privately, via the send-email function), and toasts.

export interface EmailComposeModalProps {
  title: string;
  // Only recipients WITH an email; the caller filters and (optionally)
  // reports how many were skipped for not having one.
  recipients: Recipient[];
  skippedNoEmail?: number;
  defaultSubject: string;
  defaultMessage: string;
  // Fixed attachments shown as read-only chips (e.g. the waiver PDF).
  presetAttachments?: EmailAttachment[];
  presetAttachmentLabel?: string;
  // Allow attaching an ad-hoc file picked in the browser.
  allowAttachment?: boolean;
  sendLabel?: string;
  onClose: () => void;
  onSent?: (result: SendEmailResult) => void;
}

export function EmailComposeModal({
  title,
  recipients,
  skippedNoEmail = 0,
  defaultSubject,
  defaultMessage,
  presetAttachments = [],
  presetAttachmentLabel,
  allowAttachment = false,
  sendLabel,
  onClose,
  onSent,
}: EmailComposeModalProps) {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const count = recipients.length;
  const recipientSummary = useMemo(() => {
    if (count === 0) return 'No recipients have an email on file.';
    const names = recipients
      .map((r) => r.name?.trim() || r.email)
      .slice(0, 3)
      .join(', ');
    const more = count > 3 ? ` +${count - 3} more` : '';
    return `${count} recipient${count === 1 ? '' : 's'}: ${names}${more}`;
  }, [recipients, count]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (count === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const attachments = [...presetAttachments];
      if (file) attachments.push(await fileToAttachment(file));
      const result = await sendEmail({
        recipients,
        subject: subject.trim(),
        message: message.trim(),
        attachments: attachments.length ? attachments : undefined,
      });
      if (result.sent > 0) {
        toast.success(
          `Email sent to ${result.sent} ${result.sent === 1 ? 'person' : 'people'}${
            result.failures?.length ? ` (${result.failures.length} failed)` : ''
          }.`,
        );
        onSent?.(result);
        onClose();
      } else {
        setErr(result.message || 'Nothing was sent.');
      }
    } catch (e2) {
      setErr(
        `${errorMessage(e2)} — if this is the first send, make sure the send-email function is deployed and its Resend key is set (see README).`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit}>
        <p className="muted small" style={{ marginTop: 0 }}>
          {recipientSummary}
          {skippedNoEmail > 0 && ` · ${skippedNoEmail} without an email will be skipped.`}
        </p>
        <p className="muted small" style={{ marginTop: '-0.4rem' }}>
          Everyone is emailed individually — addresses stay private.
        </p>

        <div className="field">
          <label htmlFor="ec-subject">Subject</label>
          <input
            id="ec-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="ec-message">Message</label>
          <textarea
            id="ec-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ minHeight: 150 }}
            required
          />
        </div>

        {presetAttachments.length > 0 && (
          <div className="field">
            <label>Attached</label>
            <div className="row wrap" style={{ gap: '0.4rem' }}>
              {presetAttachments.map((a) => (
                <span className="pill" key={a.filename}>
                  📎 {presetAttachmentLabel ?? a.filename}
                </span>
              ))}
            </div>
          </div>
        )}

        {allowAttachment && (
          <div className="field">
            <label>Attachment (optional)</label>
            <input
              ref={fileInput}
              type="file"
              accept=".pdf,application/pdf,image/*,.doc,.docx"
              style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <div className="row wrap">
              {file ? (
                <>
                  <span className="pill">📎 {file.name}</span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setFile(null);
                      if (fileInput.current) fileInput.current.value = '';
                    }}
                  >
                    Remove
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => fileInput.current?.click()}
                >
                  📎 Attach a file…
                </button>
              )}
            </div>
          </div>
        )}

        {err && <p className="error-text">{err}</p>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || count === 0 || !subject.trim() || !message.trim()}
          >
            {busy ? 'Sending…' : (sendLabel ?? `Send to ${count}`)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
